import { __decorate } from "tslib";
import { ChameleonElementMixin } from '../common/mixin';
import { BaseCalendar } from './BaseCalendar';
import { attr } from '@microsoft/fast-element';
export class CalendarComponent extends ChameleonElementMixin(BaseCalendar) {
    constructor() {
        super(...arguments);
        this.selectedDates = '';
    }
    connectedCallback() {
        super.connectedCallback();
        if (!this.isConnected) {
            return;
        }
        this.focusCurrentOrSelectedDate();
        requestAnimationFrame(() => {
            this.setAriaLabelForCells();
        });
    }
    goToPreviousMonthListener(dayDiff, shouldFocusDate = false) {
        var _a;
        const dayCells = Array.from((_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelectorAll('.day'));
        dayCells
            .filter((cell) => cell.getAttribute('tabindex') === '0')
            .forEach((cell) => {
            cell.setAttribute('tabindex', '-1');
        });
        const previousMonth = this.getMonthInfo(this.month).previous;
        if (this.year !== previousMonth.year) {
            this.year = previousMonth.year;
        }
        this.month = previousMonth.month;
        const dayToFocus = dayDiff ? previousMonth.length + dayDiff : previousMonth.length;
        requestAnimationFrame(() => {
            this.setAriaLabelForCells();
            const cell = this.findCellElement({ day: dayToFocus, month: this.month, year: this.year });
            cell === null || cell === void 0 ? void 0 : cell.setAttribute('tabindex', '0');
            if (shouldFocusDate) {
                cell === null || cell === void 0 ? void 0 : cell.focus();
            }
        });
    }
    goToNextMonthListener(dayDiff, shouldFocusDate = false) {
        var _a;
        const dayCells = Array.from((_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelectorAll('.day'));
        dayCells
            .filter((cell) => cell.getAttribute('tabindex') === '0')
            .forEach((cell) => {
            cell.setAttribute('tabindex', '-1');
        });
        const nextMonth = this.getMonthInfo(this.month).next;
        if (this.year !== nextMonth.year) {
            this.year = nextMonth.year;
        }
        this.month = nextMonth.month;
        const dayToFocus = dayDiff ? dayDiff : 1;
        requestAnimationFrame(() => {
            this.setAriaLabelForCells();
            const cell = this.findCellElement({ day: dayToFocus, month: this.month, year: this.year });
            cell === null || cell === void 0 ? void 0 : cell.setAttribute('tabindex', '0');
            if (shouldFocusDate) {
                cell === null || cell === void 0 ? void 0 : cell.focus();
            }
        });
    }
    todayButtonListener() {
        const date = new Date();
        const today = {
            day: date.getDate(),
            month: date.getMonth() + 1,
            year: date.getFullYear(),
        };
        this.month = today.month;
        this.year = today.year;
        this.selectDate(today);
    }
    handleChangeListener(event) {
        // We need to stop propagation here or the onChange for the DatePicker will trigger twice
        event.stopPropagation();
    }
    handleClickListener(event) {
        const date = event === null || event === void 0 ? void 0 : event.detail;
        if (date) {
            const { day, month, year } = date;
            this.selectDate({ day, month, year });
        }
    }
    /**
     * Handle keydown only on calendar cells
     * @param event KeyboarEvent
     * @param date information on the date targeted
     * @returns if event is handled or not
     */
    handleKeydown(event, date) {
        const { key } = event;
        const { day, month, year } = date;
        let dateToFocus = { day, month, year };
        if (key.includes('Arrow')) {
            this.unfocusDate(date);
            const dayModifiers = {
                ArrowRight: 1,
                ArrowLeft: -1,
                ArrowDown: 7,
                ArrowUp: -7,
            };
            const dayToFocus = day + dayModifiers[key];
            if (dayToFocus <= 0) {
                const dayDiff = dayToFocus;
                this.goToPreviousMonthListener(dayDiff, true);
            }
            const lastDayOfMonth = this.getMonthInfo(this.month).length;
            if (dayToFocus > lastDayOfMonth) {
                const dayDiff = dayToFocus - lastDayOfMonth;
                this.goToNextMonthListener(dayDiff, true);
            }
            dateToFocus = {
                day: dayToFocus,
                month: month,
                year: year,
            };
            this.focusDate(dateToFocus);
            return false;
        }
        if (key === 'Enter') {
            this.selectDate(dateToFocus);
            return true;
        }
        return true;
    }
    focusCurrentOrSelectedDate() {
        if (this.selectedDates) {
            this.focusDate(this.dateStringToDateObject(this.selectedDates));
        }
        else {
            this.focusDate(this.dateStringToDateObject(this.getCurrentDate()));
        }
    }
    selectDate({ day, month, year }) {
        this.selectedDates = `${month}-${day}-${year}`;
        this.month = month;
        this.year = year;
        this.focusDate({ day, month, year });
        this.emitChangeEvent({ date: { day, month, year }, selectedDates: this.selectedDates });
    }
    focusDate(date) {
        // We have to use setTimeout here to delay focusing, otherwise when the Calendar is in a Popover (eg DatePicker)
        // the Popover will move the focus after this has been called, which results in the focus not being on the current/selected date
        // but on the Popover itself.
        setTimeout(() => {
            const cellElement = this.findCellElement(date);
            cellElement === null || cellElement === void 0 ? void 0 : cellElement.setAttribute('tabindex', '0');
            cellElement === null || cellElement === void 0 ? void 0 : cellElement.focus();
        }, 0);
    }
    unfocusDate(date) {
        requestAnimationFrame(() => {
            const cellElement = this.findCellElement(date);
            cellElement === null || cellElement === void 0 ? void 0 : cellElement.setAttribute('tabindex', '-1');
            cellElement === null || cellElement === void 0 ? void 0 : cellElement.blur();
        });
    }
    emitChangeEvent(emitProps) {
        this.$emit('change', emitProps, { bubbles: false });
    }
    findCellElement({ day, month, year }) {
        var _a;
        const dayCells = Array.from((_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelectorAll('[part="day"]'));
        const cellElement = dayCells === null || dayCells === void 0 ? void 0 : dayCells.find((d) => {
            var _a;
            const dateQuery = (_a = d.querySelector('slot')) === null || _a === void 0 ? void 0 : _a.name;
            const dateToFocus = `${month}-${day}-${year}`;
            return dateQuery === dateToFocus;
        });
        return cellElement;
    }
    dateStringToDateObject(dateString) {
        const date = new Date(dateString);
        // Months are indexed from zero so we need to add one.
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const year = date.getFullYear();
        return { day, month, year };
    }
    setAriaLabelForCells() {
        var _a;
        const dayCells = Array.from((_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelectorAll('.day'));
        const days = this.getDays().flatMap((e) => e);
        for (let i = 0; i < days.length; i++) {
            const { day, month, year } = days[i];
            const cell = dayCells[i];
            const date = new Date(`${month}-${day}-${year}`);
            cell.setAttribute('aria-label', new Intl.DateTimeFormat(this.locale, { weekday: 'long', month: 'long', day: 'numeric' }).format(date));
        }
    }
    getCurrentDate() {
        const date = new Date();
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
}
__decorate([
    attr({ attribute: 'selected-dates' })
], CalendarComponent.prototype, "selectedDates", void 0);
